import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea
} from '@bliks-io/ui';
import type { PartnerContact } from '@bliks-io/contracts';

interface NoInterestDialogProps {
  contact: PartnerContact | null;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (contact: PartnerContact, note: string) => void;
}

/**
 * Closes a contact as "no interest". The note is offered for an edit first,
 * so the reason is on record when someone looks at the contact again.
 */
export function NoInterestDialog({
  contact,
  isSubmitting,
  onClose,
  onConfirm
}: NoInterestDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog open={contact !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        {contact && (
          // Keyed so each opening starts from that contact's note.
          <NoInterestForm
            key={contact._id}
            contact={contact}
            isSubmitting={isSubmitting}
            onClose={onClose}
            onConfirm={onConfirm}
            title={t('partner.contacts.noInterest.title', {
              name: `${contact.firstName} ${contact.lastName}`
            })}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function NoInterestForm({
  contact,
  isSubmitting,
  onClose,
  onConfirm,
  title
}: Omit<NoInterestDialogProps, 'contact'> & { contact: PartnerContact; title: string }) {
  const { t } = useTranslation();
  const [note, setNote] = useState(contact.note ?? '');

  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{t('partner.contacts.noInterest.description')}</DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-2">
        <Label htmlFor="no-interest-note">{t('partner.contacts.noInterest.note')}</Label>
        <Textarea
          id="no-interest-note"
          rows={4}
          value={note}
          placeholder={t('partner.contacts.noInterest.notePlaceholder')}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => onConfirm(contact, note)}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {t('partner.contacts.noInterest.confirm')}
        </Button>
      </DialogFooter>
    </>
  );
}
